/**
 * Campaign Report → Impact. FEAT-ATTRIB-003 `A2` / `S1`.
 *
 * The cheapest, highest-trust landing spot for attribution, and the plan's
 * reason is worth restating: this page already renders campaign **spend** and a
 * campaign **conversion count** side by side, from unrelated sources, with
 * **no cost per conversion derived** from them. The numbers are already in
 * front of the marketer; what is missing is the one that connects them.
 *
 * ## The governing rule, from MRC §2.3.4.3 and §2.3.4.4
 *
 * Every number carries its evidence label, its coverage and its uncertainty,
 * and the single-platform qualifier is ALWAYS on screen. Those are not
 * decorations — `meta.disclosure` is a required part of the service's payload
 * (`M1`), and the qualifier states that figures here are valid for comparing
 * xNotify channels with one another and **invalid** for comparing xNotify
 * against other media (`M2`). A panel that dropped them would publish a
 * number the standard does not permit us to publish.
 *
 * ## Nothing here computes
 *
 * Observed, credited and residual are displayed exactly as the service
 * returned them. The three ledgers are permanently separate by design, so
 * deriving one from the others in the client — "residual = observed −
 * credited" — would reintroduce the conflation the separation exists to
 * prevent, and would do it in a place no attribution test can see. The only
 * arithmetic is formatting.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, AlertIcon, Badge, Box, Button, Divider, Flex, HStack, Heading,
    SimpleGrid, Spinner, Stat, StatLabel, StatNumber, StatHelpText,
    Table, Tbody, Td, Text, Th, Thead, Tr, VStack,
} from '@chakra-ui/react';

import { fetchImpactSummary, fetchReadiness, attributionConfigured } from '../../../api/attribution';
import { buildMeasurabilityView, MEASURABILITY_STATE } from './measurabilityView';
/**
 * Every number displayed below comes from `buildImpactView`, which is pure and
 * exhaustively tested. The formatters used to live in this file and could not
 * be tested here: `react-scripts`'s Jest resolver does not honour Chakra's
 * `exports` map, so no Chakra component in this app renders under test. Moving
 * the mapping out is what makes the risky part — which figure feeds which
 * slot, and absent-versus-zero — assertable at all.
 */
import { buildImpactView, VIEW_STATE } from './impactView';

const LABEL_TONE = {
    observed: 'green',
    rule_attributed: 'blue',
    modeled_attributed: 'purple',
    observational_effect_estimate: 'orange',
    randomized_experimental: 'teal',
};

function EvidenceBadge({ label }) {
    if (!label) return null;
    return (
        <Badge colorScheme={LABEL_TONE[label] ?? 'gray'} fontSize="0.65rem" textTransform="none">
            {label}
        </Badge>
    );
}

/**
 * §6 `S2` — can this tenant even be measured?
 *
 * `DA-BUG-ATTR-0263`: §6 promises four display surfaces and §11 sequenced two,
 * so this one was named and never built. It discharges MRC §2.3.4.3's
 * "minimum data sample sizes" as a product surface rather than an opinion in a
 * document, and **no competitor publishes it**.
 *
 * It lives inside this tab rather than on its own route because a new page
 * needs `tbl_pages` and `tbl_page_permissions` seeded per tenant — a migration
 * and a config change, far heavier than the surface warrants.
 */
function MeasurabilityPanel({ payload }) {
    const m = buildMeasurabilityView(payload, { configured: attributionConfigured() });

    if (m.state === MEASURABILITY_STATE.NOT_CONFIGURED) return null;

    if (m.state === MEASURABILITY_STATE.UNAVAILABLE) {
        return (
            <Box>
                <Heading size="sm" mb={2}>Measurability</Heading>
                <Text fontSize="sm" color="gray.700">
                    Not available: <b>{m.reason}</b>
                </Text>
                {m.message && (
                    <Text fontSize="sm" color="gray.600">{m.message}</Text>
                )}
            </Box>
        );
    }

    return (
        <Box>
            <HStack spacing={2} mb={3}>
                <Heading size="sm">Measurability — can a holdout prove lift here?</Heading>
                <Badge
                    colorScheme={m.verdict === 'measurable' ? 'green' : 'orange'}
                    variant="subtle"
                >
                    {m.verdict}
                </Badge>
            </HStack>

            {m.refusalReason ? (
                /*
                 * A refusal is the whole answer. P0.2's acceptance is
                 * explicit: a zero-touch tenant reports
                 * `no_sends_in_the_window`, NOT 0 — because "sends nothing"
                 * and "holdout detects nothing" are different findings and a
                 * zero reads as the second.
                 */
                <Text fontSize="sm" color="gray.700">
                    Cannot be computed: <b>{m.refusalReason}</b>
                </Text>
            ) : (
                <>
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={4}>
                        <Stat>
                            <StatLabel>Recommended holdout</StatLabel>
                            <StatNumber>{m.recommendedHoldout ?? 'none'}</StatNumber>
                            <StatHelpText>
                                {m.recommendedHoldout
                                    ? 'Detects the lift that matters'
                                    : 'NO offered holdout detects the lift that matters — '
                                      + 'the figures below are what each one could prove'}
                            </StatHelpText>
                        </Stat>
                        <Stat>
                            <StatLabel>Experiment units</StatLabel>
                            <StatNumber>{m.experimentUnits}</StatNumber>
                            <StatHelpText>
                                of {m.subjects} at risk · touched {m.touchCoverage}
                            </StatHelpText>
                        </Stat>
                        <Stat>
                            <StatLabel>Subjects required</StatLabel>
                            <StatNumber>{m.subjectsRequired ?? '—'}</StatNumber>
                            <StatHelpText>
                                {m.baseline
                                    ? `baseline ${m.baseline.rate} (${m.baseline.basis})`
                                    : 'baseline not stated'}
                            </StatHelpText>
                        </Stat>
                    </SimpleGrid>

                    <Table size="sm" variant="simple">
                        <Thead>
                            <Tr>
                                <Th>Holdout</Th>
                                <Th isNumeric>Control</Th>
                                <Th isNumeric>Treatment</Th>
                                <Th isNumeric>Smallest detectable lift</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {m.holdouts.map((h) => (
                                <Tr key={h.key}>
                                    <Td>{h.holdout}</Td>
                                    <Td isNumeric>{h.control}</Td>
                                    <Td isNumeric>{h.treatment}</Td>
                                    <Td isNumeric>{h.mdeRelative}</Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>

                    {/* MRC §2.3.4.3 — the test and its parameters, disclosed. */}
                    <Text fontSize="xs" color="gray.600" mt={2}>
                        {m.assumptions.test} · alpha {m.assumptions.alpha} · power{' '}
                        {m.assumptions.power} · asked of a{' '}
                        {m.assumptions.usefulRelativeLift} relative lift
                    </Text>
                </>
            )}
        </Box>
    );
}

export default function ImpactTab({ goalDefinitionId }) {
    const [state, setState] = useState({ loading: true, payload: null, readiness: null });

    const load = useCallback(async (signal) => {
        setState({ loading: true, payload: null, readiness: null });
        /**
         * Both reads in parallel, and `allSettled` so one cannot take the
         * other down: §6's `S2` panel answers "can this tenant be measured?"
         * and is useful precisely when the summary is thin. `fetchReadiness`
         * already converts a refusal into a payload rather than throwing, so
         * a rejection here means a transport failure.
         */
        const [summary, readiness] = await Promise.allSettled([
            fetchImpactSummary({ goalDefinitionId, dimension: 'channel', signal }),
            fetchReadiness({ goalDefinitionId, signal }),
        ]);
        setState({
            loading: false,
            payload: summary.status === 'fulfilled' ? summary.value : null,
            readiness: readiness.status === 'fulfilled' ? readiness.value : null,
        });
    }, [goalDefinitionId]);

    useEffect(() => {
        const ctrl = new AbortController();
        load(ctrl.signal);
        return () => ctrl.abort();
    }, [load]);

    if (state.loading) {
        return (
            <Flex align="center" gap={3} py={10} justify="center">
                <Spinner size="sm" />
                <Text fontSize="sm" color="gray.600">Loading attribution…</Text>
            </Flex>
        );
    }

    const v = buildImpactView(state.payload, { configured: attributionConfigured() });

    if (v.state === VIEW_STATE.NOT_CONFIGURED) {
        /**
         * Said plainly rather than shown as an error. An unconfigured service
         * is a deployment fact, and naming the variable is the whole point —
         * the failure this replaces was a 404 against `undefined/...` with
         * nothing on screen naming the cause.
         */
        return (
            <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Box>
                    <Text fontWeight="medium">{v.message}</Text>
                    <Text fontSize="sm">
                        Set <code>{v.envVar}</code> and rebuild. See <code>.env.example</code>.
                    </Text>
                </Box>
            </Alert>
        );
    }

    if (v.state === VIEW_STATE.REFUSED) {
        /**
         * A refusal carries its OWN reason, and showing it is the product.
         * `label_not_permitted_in_this_state` means the tenant has not earned
         * a modelled number yet — the maturity gate working, not an error.
         */
        return (
            <Alert status="warning" borderRadius="md" alignItems="flex-start">
                <AlertIcon />
                <Box>
                    <Text fontWeight="medium">No attribution number is published for this goal.</Text>
                    <Text fontSize="sm">{v.message}</Text>
                    {v.error ? <Badge mt={1} fontSize="0.65rem">{v.error}</Badge> : null}
                    <Button size="xs" mt={3} onClick={() => load()}>Retry</Button>
                </Box>
            </Alert>
        );
    }

    return (
        <VStack align="stretch" spacing={6}>
            {/* The three ledgers, side by side and never derived from one another. */}
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <Stat>
                    <StatLabel>
                        <HStack spacing={2}>
                            <Text>Observed conversions</Text>
                            <EvidenceBadge label="observed" />
                        </HStack>
                    </StatLabel>
                    <StatNumber>{v.observed.conversions}</StatNumber>
                    <StatHelpText>
                        {v.observed.value}
                        {v.observed.unavailableReason
                            ? ` · unavailable: ${v.observed.unavailableReason}` : ''}
                    </StatHelpText>
                </Stat>

                <Stat>
                    <StatLabel>
                        <HStack spacing={2}>
                            <Text>Credited</Text>
                            <EvidenceBadge label={v.evidenceLabel} />
                        </HStack>
                    </StatLabel>
                    <StatNumber>{v.credited.share}</StatNumber>
                    <StatHelpText>{v.credited.value}</StatHelpText>
                </Stat>

                <Stat>
                    <StatLabel>Residual (unattributed)</StatLabel>
                    <StatNumber>{v.residual.share}</StatNumber>
                    <StatHelpText>{v.residual.value} · {v.residual.abstained} abstained</StatHelpText>
                </Stat>
            </SimpleGrid>

            <Divider />

            {/*
              * Phase A's fourth figure — DA-BUG-ATTR-0264.
              *
              * Its own row rather than a fourth column, because it is not a
              * ledger: the three above are shares of one conserved allocation
              * and this is money spent. Putting it beside them invites the
              * reader to divide one by another.
              */}
            <Box>
                <HStack spacing={2} mb={3}>
                    <Heading size="sm">Cost per attributed conversion</Heading>
                    <EvidenceBadge label={v.evidenceLabel} />
                </HStack>

                {v.cost.available ? (
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                        <Stat>
                            <StatLabel>Per attributed conversion</StatLabel>
                            <StatNumber>{v.cost.perConversion}</StatNumber>
                            <StatHelpText>
                                {v.cost.isLowerBound
                                    ? 'A LOWER BOUND — only the priced sends are counted, '
                                      + 'and the figure is deliberately not scaled up'
                                    : 'Every credited send is priced'}
                            </StatHelpText>
                        </Stat>
                        <Stat>
                            <StatLabel>Spend on credited sends</StatLabel>
                            <StatNumber>{v.cost.totalSpend}</StatNumber>
                            <StatHelpText>
                                {v.cost.conversions} attributed conversions
                            </StatHelpText>
                        </Stat>
                        <Stat>
                            <StatLabel>Cost coverage</StatLabel>
                            <StatNumber>{v.cost.coverage}</StatNumber>
                            <StatHelpText>
                                {v.cost.unpriced.length === 0
                                    ? 'No unpriced sends'
                                    : v.cost.unpriced
                                        .map((u) => `${u.touches} ${u.reason}`).join(' · ')}
                            </StatHelpText>
                        </Stat>
                    </SimpleGrid>
                ) : (
                    <Box>
                        <Text fontSize="sm" color="gray.700">
                            Not published: <b>{v.cost.reason}</b>
                        </Text>
                        {/*
                          * The coverage and the reasons survive the refusal.
                          * At zero coverage "0 of 4,312 priced, all
                          * no_active_billing_sheet" IS the finding, and a
                          * blank cell sends the reader looking for it.
                          */}
                        <Text fontSize="sm" color="gray.600">{v.cost.coverage}</Text>
                        {v.cost.unpriced.length > 0 && (
                            <Text fontSize="sm" color="gray.600">
                                {v.cost.unpriced.map((u) => `${u.touches} ${u.reason}`).join(' · ')}
                            </Text>
                        )}
                    </Box>
                )}
            </Box>

            <Divider />

            <MeasurabilityPanel payload={state.readiness} />

            <Divider />

            <Box>
                <Heading size="sm" mb={3}>Credit by channel</Heading>
                {v.breakdown.length === 0 ? (
                    <Text fontSize="sm" color="gray.600">No breakdown rows for this generation.</Text>
                ) : (
                    <Table size="sm" variant="simple">
                        <Thead>
                            <Tr>
                                <Th>Channel</Th>
                                <Th isNumeric>Credited units</Th>
                                <Th isNumeric>Credited value</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {v.breakdown.map((row) => (
                                <Tr key={row.key}>
                                    <Td>{row.label}</Td>
                                    <Td isNumeric>{row.units}</Td>
                                    <Td isNumeric>{row.value}</Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                )}
            </Box>

            {/* Coverage, because a number without it cannot be judged. */}
            {v.coverageUnavailableReason ? (
                <Alert status="info" borderRadius="md" fontSize="sm">
                    <AlertIcon />
                    Coverage is unavailable: {v.coverageUnavailableReason}.
                </Alert>
            ) : null}

            <Divider />

            {/* What this number is and is NOT — the condition Phase A ships under. */}
            {v.causal ? (
                <Alert
                    status={v.causal.permitted ? 'success' : 'warning'}
                    borderRadius="md"
                    alignItems="flex-start"
                >
                    <AlertIcon />
                    <Box>
                        <Text fontWeight="medium">
                            {v.causal.permitted
                                ? 'This is a measured causal effect.'
                                : 'This is credit, not incremental value.'}
                        </Text>
                        {v.causal.headlineIs ? (
                            <Text fontSize="sm" mt={1}>{v.causal.headlineIs}</Text>
                        ) : null}
                        {v.causal.notThis ? (
                            <Text fontSize="sm" mt={2} fontWeight="medium">{v.causal.notThis}</Text>
                        ) : null}
                        {v.causal.usableFor.length > 0 ? (
                            <Text fontSize="sm" mt={2}>
                                <b>Safe to use for:</b> {v.causal.usableFor.join('; ')}.
                            </Text>
                        ) : null}
                        {!v.causal.permitted && v.causal.whatWouldChangeIt ? (
                            <Text fontSize="xs" mt={2} color="gray.600">
                                {v.causal.whatWouldChangeIt}
                            </Text>
                        ) : null}
                    </Box>
                </Alert>
            ) : null}

            {/* MRC §2.3.4.3 — the methodology disclosure, in non-technical terms. */}
            <Box bg="gray.50" borderRadius="md" p={4}>
                <Heading size="xs" mb={2}>How this was measured</Heading>
                <VStack align="stretch" spacing={1} fontSize="sm" color="gray.700">
                    <Text><b>Model:</b> {v.disclosure.modelUsed}</Text>
                    <Text><b>Reliance on models:</b> {v.disclosure.reliance}</Text>
                    <Text><b>Data sources:</b> {v.disclosure.sources}</Text>
                    {v.disclosure.lookbackDays !== null ? (
                        <Text><b>Lookback window:</b> {v.disclosure.lookbackDays} days</Text>
                    ) : null}
                </VStack>

                {/* MRC §2.3.4.4 — always on screen, never conditional. */}
                {v.qualifiers.map((q) => (
                    <Alert key={q.id} status="warning" borderRadius="md" mt={3} fontSize="sm" alignItems="flex-start">
                        <AlertIcon />
                        <Box>
                            <Text>{q.text}</Text>
                            <Text mt={1} fontSize="xs" color="gray.600">{q.standard}</Text>
                        </Box>
                    </Alert>
                ))}
            </Box>
        </VStack>
    );
}
